<script lang="ts">
	import * as Sidebar from '$shared/components/ui/sidebar/index.js';
	import DocsHeader from '$lib/components/docs/DocsHeader.svelte';
	import DocsSidebar from '$lib/components/docs/DocsSidebar.svelte';
	import { docsNavigation, getFirstPage } from '$lib/data/docs-data';

	const firstPage = getFirstPage();
</script>

<svelte:head>
	<title>Documentation - Corral AI</title>
	<meta name="description" content="Learn how to deploy, configure, and manage AI agents with Corral AI." />
</svelte:head>

<div class="docs-theme h-screen bg-white flex flex-col">
	<DocsHeader />

	<Sidebar.Provider class="flex-1 min-h-0">
		<div class="flex flex-1 h-full overflow-hidden">
			<!-- Sidebar navigation -->
			<DocsSidebar />

			<!-- Main content area -->
			<Sidebar.Inset class="flex-1 overflow-y-auto pr-4">
				<div class="px-6 lg:px-12 py-12">
					<!-- Hero -->
					<div class="mb-12">
						<h1 class="font-display font-[350] text-[42px] lg:text-[52px] text-[#020202] tracking-[-0.02em] leading-[1.1] mb-4">
							Documentation
						</h1>
						<p class="font-body text-[18px] text-[#5c5855] max-w-2xl">
							Learn how to deploy, configure, and manage AI agents with Corral AI. Get started with our quickstart guide or explore specific topics.
						</p>

						<!-- Quick start CTA -->
						{#if firstPage}
							<a
								href="/docs/{firstPage.slug}"
								class="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-[#020202] text-white font-body font-medium text-[14px] rounded-md hover:bg-[#333] transition-colors"
							>
								Get Started
								<svg class="w-4 h-4" viewBox="0 0 16 16" fill="none">
									<path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
								</svg>
							</a>
						{/if}
					</div>

					<!-- Section cards -->
					<div class="grid gap-6 md:grid-cols-2">
						{#each docsNavigation as section}
							<a
								href="/docs/{section.pages[0]?.slug}"
								class="group block p-6 bg-[#fafafa] border border-[#e5e5e5] rounded-lg hover:border-[#ee6018] hover:bg-white transition-colors"
							>
								<h2 class="font-display font-[350] text-[20px] text-[#020202] mb-2 group-hover:text-[#ee6018] transition-colors">
									{section.title}
								</h2>
								<p class="font-body text-[14px] text-[#5c5855] mb-4">
									{section.pages.length} {section.pages.length === 1 ? 'article' : 'articles'}
								</p>
								<ul class="space-y-1">
									{#each section.pages.slice(0, 3) as page}
										<li class="font-body text-[13px] text-[#878787]">
											{page.title}
										</li>
									{/each}
									{#if section.pages.length > 3}
										<li class="font-mono text-[12px] text-[#878787]">
											+{section.pages.length - 3} more
										</li>
									{/if}
								</ul>
							</a>
						{/each}
					</div>
				</div>
			</Sidebar.Inset>
		</div>
	</Sidebar.Provider>
</div>

<style>
	/* Minimal scrollbar styling for docs pages */
	:global(.docs-theme) {
		scrollbar-width: thin;
		scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
	}

	:global(.docs-theme *) {
		scrollbar-width: thin;
		scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
	}

	:global(.docs-theme ::-webkit-scrollbar) {
		width: 6px;
		height: 6px;
	}

	:global(.docs-theme ::-webkit-scrollbar-track) {
		background: transparent;
	}

	:global(.docs-theme ::-webkit-scrollbar-thumb) {
		background-color: rgba(0, 0, 0, 0.15);
		border-radius: 3px;
	}

	:global(.docs-theme ::-webkit-scrollbar-thumb:hover) {
		background-color: rgba(0, 0, 0, 0.25);
	}
</style>
